import { useCreditBalance } from '~/hooks/useCreditBalance';

/** Format a credit count with thousands separators (e.g. 10000 → "10,000"). */
function formatCredits(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Account balance summary at the TOP of the avatar dropdown (above Profile). A "Credits" header (with the
 * usage % on the right) sits over a linear progress bar showing how much of the granted credits have been
 * USED — it fills as credits are spent (Cursor-style) — with a "used / total" label below and, when the
 * user has purchased top-ups, a "+N add-on" count on that row's right. Reads the same `useCreditBalance`
 * the avatar ring uses (`credits` + `grantedCredits` total + `topupCredits`, all real values from the API).
 */
export function AccountBalanceSection() {
  const { credits, grantedCredits, topupCredits, isLoading } = useCreditBalance();
  const remaining = credits ?? grantedCredits;

  const hasData = grantedCredits != null && grantedCredits > 0 && remaining != null;
  // The meter tracks the MONTHLY allocation; purchased top-up credits sit above it (shown as "+N").
  const monthlyRemaining = Math.max((remaining ?? 0) - topupCredits, 0);
  // Usage counting up (0..1): how much of the monthly allocation has been spent. Before data loads (and
  // on a fresh cycle) the bar is empty, so it reads as "start using it" rather than "fully spent".
  const used = hasData ? Math.max(grantedCredits - monthlyRemaining, 0) : 0;
  const fraction = hasData ? Math.min(used, grantedCredits) / grantedCredits : 0;

  const usedPercent = Math.round(fraction * 100);

  return (
    <div className="flex flex-col gap-[var(--spacing-8)] px-[var(--spacing-6)] py-[var(--spacing-8)]">
      {/* Header row: section title + usage percentage on the right. */}
      <div className="flex items-center justify-between gap-[var(--spacing-8)]">
        <span className="text-sm font-medium text-[var(--color-text-default)]">Credits</span>
        <span className="text-sm text-[var(--color-text-subtle)]">
          {isLoading || !hasData ? '—' : `${usedPercent}%`}
        </span>
      </div>

      {/* Linear progress bar (usage of granted). */}
      <div className="flex items-center gap-[var(--spacing-10)]">
        <div className="h-[6px] flex-1 overflow-hidden rounded-full bg-[var(--color-switch-default)]">
          <div
            className="h-full rounded-full bg-[var(--color-state-primary)] transition-[width] duration-300 ease-out"
            style={{ width: `${usedPercent}%` }}
          />
        </div>
      </div>

      {/* Used / total label, with add-on credits ("+N add-on", purchased & non-expiring) on the right. */}
      <div className="flex items-center justify-between gap-[var(--spacing-8)]">
        <span className="text-sm text-[var(--color-text-subtle)]">
          {isLoading || !hasData ? '—' : `${formatCredits(used)} / ${formatCredits(grantedCredits)} used`}
        </span>
        {hasData && topupCredits > 0 && (
          <span className="text-sm text-[var(--color-text-muted)]">+{formatCredits(topupCredits)} add-on</span>
        )}
      </div>

      {/* Full-width separator between the balance summary and the menu items. Negative margin cancels
          this section's px-6 AND the dropdown content's p-4 so the line spans the whole panel edge-to-edge. */}
      <div className="-mx-[var(--spacing-10)] mt-[var(--spacing-2)] h-px bg-[var(--color-border-default)]" />
    </div>
  );
}
